import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { IamService } from '../../../../module/iam/iam.service';
import { UserModel } from '../../../../module/iam/user-settings/model/user.model';
import { PaginatedModel } from '../../select/pagination/model/paginated.model';

@Component({
    selector: 'app-prescriber-array-input',
    templateUrl: './prescriber-array-input.component.html',
    styleUrl: './prescriber-array-input.component.scss'
})
export class PrescriberArrayInputComponent implements OnInit, OnDestroy {
    private _value: string[] = [];
    private initialized = false;
    private request = new Subscription();
    private subscriptions = new Subscription();
    private requestedPrescriberIds = new Set<string>();
    private selectedPrescriberIds = new Set<string>();
    private prescribersById = new Map<string, UserModel>();

    @Input()
    set value(value: string[]) {
        this._value = value ?? [];
        this.selectedPrescriberIds = new Set(this._value);
        if (this.initialized) {
            this.initializePrescribers();
        }
    }
    get value(): string[] {
        return this._value;
    }

    @Input() placeholder = 'Selecione os prescritores';
    @Output() changed = new EventEmitter<string[]>();

    @ViewChild('trigger') trigger!: ElementRef;
    @ViewChild('dropdownTemplate') menuTemplate!: TemplateRef<unknown>;

    overlayRef?: OverlayRef;
    prescribers: UserModel[] = [];
    searching = true;

    constructor(
        private readonly iamService: IamService,
        private readonly overlay: Overlay,
        private readonly viewContainerRef: ViewContainerRef
    ) {}

    public ngOnInit(): void {
        this.initialized = true;
        this.searchPrescribers('');
        this.initializePrescribers();
    }

    public ngOnDestroy(): void {
        this.request.unsubscribe();
        this.subscriptions.unsubscribe();
        this.overlayRef?.dispose();
    }

    public eventOpenOptions(): void {
        if (this.searching) {
            return;
        }
        this.overlayRef?.dispose();

        const positionStrategy = this.overlay.position()
            .flexibleConnectedTo(this.trigger)
            .withPositions([{
                originX: 'start',
                originY: 'bottom',
                overlayX: 'start',
                overlayY: 'top',
                offsetY: 8
            }]);

        const triggerWidth = this.trigger.nativeElement.getBoundingClientRect().width;
        this.overlayRef = this.overlay.create({
            positionStrategy,
            hasBackdrop: true,
            width: triggerWidth,
            minWidth: triggerWidth,
            backdropClass: 'cdk-overlay-transparent-backdrop',
            scrollStrategy: this.overlay.scrollStrategies.reposition()
        });
        this.overlayRef.backdropClick().subscribe(() => this.overlayRef?.dispose());
        this.overlayRef.attach(new TemplatePortal(this.menuTemplate, this.viewContainerRef));
    }

    public eventPrescriberChanged(prescriber: UserModel): void {
        if (this.hasPrescriber(prescriber._id)) {
            this.selectedPrescriberIds.delete(prescriber._id);
        } else {
            this.selectedPrescriberIds.add(prescriber._id);
            this.prescribersById.set(prescriber._id, prescriber);
        }

        this.changed.emit([...this.selectedPrescriberIds]);
        this.overlayRef?.dispose();
    }

    public eventRemovePrescriber(prescriberId: string, event: Event): void {
        event.stopPropagation();
        this.selectedPrescriberIds.delete(prescriberId);
        this.changed.emit([...this.selectedPrescriberIds]);
    }

    public eventFilterChanged(event: Event): void {
        this.searchPrescribers((event.target as HTMLInputElement).value);
    }

    public hasPrescriber(prescriberId: string): boolean {
        return this.selectedPrescriberIds.has(prescriberId);
    }

    public getSelectedPrescriberIds(): string[] {
        return [...this.selectedPrescriberIds];
    }

    public getPrescriberName(prescriberId: string): string {
        return this.prescribersById.get(prescriberId)?.name || prescriberId;
    }

    private initializePrescribers(): void {
        this.value.forEach((prescriberId) => {
            if (this.requestedPrescriberIds.has(prescriberId)) {
                return;
            }
            this.requestedPrescriberIds.add(prescriberId);
            this.subscriptions.add(this.iamService.getUser(prescriberId).subscribe({
                next: (prescriber) => this.prescribersById.set(prescriberId, prescriber),
                error: () => undefined
            }));
        });
    }

    private searchPrescribers(searchTerm: string): void {
        this.request.unsubscribe();
        this.searching = true;

        this.request = this.iamService.getUsers(searchTerm, 1, 100).pipe(
            debounceTime(600),
            distinctUntilChanged()
        ).subscribe({
            next: (users) => this.handleUsers(users),
            error: () => {
                this.prescribers = [];
                this.searching = false;
            }
        });
    }

    private handleUsers(users: PaginatedModel<UserModel>): void {
        this.prescribers = users.items.filter((user) =>
            user.roles?.some((role) => role.name === 'prescriber-base')
        );
        this.prescribers.forEach((prescriber) => this.prescribersById.set(prescriber._id, prescriber));
        this.searching = false;
    }
}
